/**
 * Build a partial booking PUT from the full draft payload.
 * Omitted `packages` must never be treated as [] — only send packages when add-ons changed.
 */

const PRICING_MONEY_LABELS = new Set([
  'Booking amount',
  'Add-ons',
  'Discount',
  'Discount code',
  'Pricing waiver'
]);

const PAYMENT_LABELS = new Set([
  'Amount paid',
  'Amount pending',
  'Payment method',
  'Transaction ID'
]);

const REQUIREMENT_KEYS_BY_LABEL = {
  'Event activity': ['activity'],
  Catering: ['catering_preference', 'has_dietary_restrictions', 'dietary_restrictions'],
  'Dietary requirements': ['has_dietary_restrictions', 'dietary_restrictions'],
  'Audio/visual (A/V)': ['needs_av'],
  Alcohol: ['alcohol_consumption'],
  Guests: [],
  'Pricing mode': [
    'booking_type',
    'pricing_mode',
    'per_person_rate',
    'space_hourly_rate',
    'base_hourly_rate',
    'base_fee'
  ],
  'Start time': ['duration_hours'],
  'End time': ['duration_hours']
};

const PRICING_REQUIREMENT_KEYS = [
  'booking_amount',
  'add_ons_total',
  'discount_code',
  'discount_amount',
  'platform_fee',
  'processing_fee',
  'amount_paid',
  'amount_pending',
  'free_mode',
  'free_booking_mode',
  'space_free',
  'last_priced_snapshot',
  'base_fee',
  'per_person_rate',
  'space_hourly_rate',
  'base_hourly_rate',
  'pricing_mode',
  'booking_type',
  'duration_hours',
  'hide_venue_pricing',
  'pricing_display'
];

const PAYMENT_REQUIREMENT_KEYS = [
  'amount_paid',
  'amount_pending',
  'transaction_id',
  'payment_method',
  'payment_date'
];

const copyDefinedKeys = (target, source, keys) => {
  if (!source || typeof source !== 'object') return;
  keys.forEach((key) => {
    if (source[key] !== undefined) {
      target[key] = source[key];
    }
  });
};

export const pickDirtyBookingUpdatePayload = ({
  fullPayload,
  pendingChanges = [],
  packagesChanged = false,
  markAsPaid = false
} = {}) => {
  const labels = new Set((pendingChanges || []).map((change) => change?.label).filter(Boolean));
  const pricingMoneyChanged = [...PRICING_MONEY_LABELS].some((label) => labels.has(label));
  const paymentChanged =
    Boolean(markAsPaid) || [...PAYMENT_LABELS].some((label) => labels.has(label));
  const guestsChanged = labels.has('Guests');
  const timesChanged = labels.has('Start time') || labels.has('End time');
  const dateChanged = labels.has('Event date');
  const modeChanged = labels.has('Pricing mode');
  const titleChanged = labels.has('Event name');
  const activityChanged = labels.has('Event activity');
  const cateringChanged = labels.has('Catering') || labels.has('Dietary requirements');
  const avChanged = labels.has('Audio/visual (A/V)');
  const alcoholChanged = labels.has('Alcohol');

  const out = {};

  if (titleChanged) out.event_name = fullPayload.event_name;
  if (dateChanged) out.booking_date = fullPayload.booking_date;
  if (labels.has('Start time')) out.start_time = fullPayload.start_time;
  if (labels.has('End time')) out.end_time = fullPayload.end_time;
  if (timesChanged && fullPayload.duration_hours != null) {
    out.duration_hours = fullPayload.duration_hours;
  }
  if (modeChanged) {
    if (fullPayload.booking_type != null) out.booking_type = fullPayload.booking_type;
    if (fullPayload.booking_mode != null) out.booking_mode = fullPayload.booking_mode;
  }
  if (guestsChanged && fullPayload.headcount != null) {
    out.headcount = fullPayload.headcount;
  }

  if (pricingMoneyChanged) {
    out.total_amount = fullPayload.total_amount;
    out.final_amount = fullPayload.final_amount;
    if (fullPayload.fee_breakdown !== undefined) out.fee_breakdown = fullPayload.fee_breakdown;
    if (fullPayload.add_ons_total !== undefined) out.add_ons_total = fullPayload.add_ons_total;
    if (fullPayload.discount_amount !== undefined) out.discount_amount = fullPayload.discount_amount;
    if (fullPayload.processing_fee !== undefined) out.processing_fee = fullPayload.processing_fee;
    if (fullPayload.hourly_rate !== undefined) out.hourly_rate = fullPayload.hourly_rate;
    out.amount_paid = fullPayload.amount_paid;
    out.amount_pending = fullPayload.amount_pending;
  }

  if (paymentChanged) {
    out.amount_paid = fullPayload.amount_paid;
    out.amount_pending = fullPayload.amount_pending;
    if (fullPayload.transaction_id !== undefined) out.transaction_id = fullPayload.transaction_id;
    if (fullPayload.payment_method !== undefined) out.payment_method = fullPayload.payment_method;
    if (fullPayload.payment_status) out.payment_status = fullPayload.payment_status;
  }

  // Only an explicit add-on edit may send packages — including packages: [].
  if (packagesChanged) {
    out.packages = Array.isArray(fullPayload.packages) ? fullPayload.packages : [];
  }

  const requirements = {};
  const fullReq = fullPayload.requirements && typeof fullPayload.requirements === 'object'
    ? fullPayload.requirements
    : {};

  Object.entries(REQUIREMENT_KEYS_BY_LABEL).forEach(([label, keys]) => {
    if (labels.has(label)) copyDefinedKeys(requirements, fullReq, keys);
  });

  if (pricingMoneyChanged) {
    copyDefinedKeys(requirements, fullReq, PRICING_REQUIREMENT_KEYS);
  }
  if (paymentChanged) {
    copyDefinedKeys(requirements, fullReq, PAYMENT_REQUIREMENT_KEYS);
  }
  if (packagesChanged && fullReq.packages !== undefined) {
    requirements.packages = fullReq.packages;
  }

  if (Object.keys(requirements).length > 0) {
    out.requirements = requirements;
  }

  if (
    !titleChanged &&
    !dateChanged &&
    !timesChanged &&
    !modeChanged &&
    !guestsChanged &&
    !activityChanged &&
    !cateringChanged &&
    !avChanged &&
    !alcoholChanged &&
    !pricingMoneyChanged &&
    !paymentChanged &&
    !packagesChanged
  ) {
    return {};
  }

  // Mark-paid must not reprice. Only payment_status + amount paid/pending.
  if (markAsPaid && !pricingMoneyChanged) {
    delete out.total_amount;
    delete out.final_amount;
    delete out.fee_breakdown;
    delete out.add_ons_total;
    delete out.discount_amount;
    delete out.processing_fee;
    delete out.hourly_rate;
    delete out.packages;
    if (out.requirements && typeof out.requirements === 'object') {
      const keep = new Set([
        'amount_paid',
        'amount_pending',
        'transaction_id',
        'payment_method',
        'payment_date'
      ]);
      Object.keys(out.requirements).forEach((key) => {
        if (!keep.has(key)) delete out.requirements[key];
      });
      if (Object.keys(out.requirements).length === 0) delete out.requirements;
    }
  }

  return out;
};

export const mergeBookingLocalUpdate = (existingBooking, payload) => {
  const existingReq =
    existingBooking?.requirements && typeof existingBooking.requirements === 'object'
      ? existingBooking.requirements
      : {};
  const next = {
    ...existingBooking,
    ...payload,
    requirements: {
      ...existingReq,
      ...(payload?.requirements || {})
    }
  };
  if (!payload || !Object.prototype.hasOwnProperty.call(payload, 'packages')) {
    if (Object.prototype.hasOwnProperty.call(existingBooking || {}, 'packages')) {
      next.packages = existingBooking.packages;
    } else {
      delete next.packages;
    }
    if (
      existingReq.packages !== undefined &&
      payload?.requirements &&
      !Object.prototype.hasOwnProperty.call(payload.requirements, 'packages')
    ) {
      next.requirements.packages = existingReq.packages;
    }
  }
  return next;
};
